import { useEffect, useRef } from 'react';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError, mapFieldErrors } from 'core/errors/mapFieldErrors';
import { Badge } from 'shared/components/Feedback';
import { useFocusTrap, useBodyScrollLock } from 'layout/useFocusTrap';
import { statusTone, statusValue } from 'features/company-plans/companyPlans.helpers';

export function StatusChip({ status, t, prefix = 'companyPlans.statuses' }) {
  const value = statusValue(status) || 'unknown';
  const label = t(`${prefix}.${value}`);
  const text = label === `${prefix}.${value}` ? value : label;
  return <Badge tone={statusTone(value)}>{text}</Badge>;
}

export function DefinitionList({ rows }) {
  return (
    <dl className="definition-grid">
      {rows.map((row) => (
        <div key={row.label} className="definition-row">
          <dt>{row.label}</dt>
          <dd>{row.value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

export function handleMutationError(error, {
  t,
  toast,
  setErrors,
  setConflict,
} = {}) {
  if (error instanceof ApiError && error.isValidation()) {
    const mapped = mapFieldErrors(error.errors);
    setErrors?.(mapped);
    toast?.error(humanizeApiError(error, t));
    return mapped;
  }
  if (error instanceof ApiError && error.isConflict()) {
    setConflict?.(error.message || t('errors.unexpected'));
    toast?.error(error.message || t('errors.unexpected'));
    return null;
  }
  if (error instanceof ApiError && error.isForbidden()) {
    toast?.error(t('errors.forbidden'));
    return null;
  }
  toast?.error(humanizeApiError(error, t));
  return null;
}

export function AdminActionDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  loading = false,
  tone = 'danger',
  children,
}) {
  const dialogRef = useRef(null);
  useFocusTrap(open, dialogRef, { onEscape: onCancel });
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const firstInput = dialogRef.current?.querySelector('input, textarea, select');
    firstInput?.focus();
    return undefined;
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div className="modal-backdrop" role="presentation" data-testid="admin-action-backdrop">
      <div
        ref={dialogRef}
        className="modal card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-action-title"
        aria-describedby="admin-action-body"
        tabIndex={-1}
      >
        <h2 id="admin-action-title">{title}</h2>
        {body ? <p id="admin-action-body">{body}</p> : <div id="admin-action-body" />}
        {children}
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`btn btn-${tone}`}
            onClick={onConfirm}
            disabled={loading}
            aria-busy={loading || undefined}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
